// =============================================================================
// app/src/lib/svgPath.ts — Serginho Autopeças
// Ler o `d` de um <path> SVG e devolver segmentos que o PDF sabe desenhar.
//
// Existe porque o orçamento impresso (17/08/2026) precisa do SÍMBOLO S no
// cabeçalho, e o gerador de PDF da casa só sabia texto, linha e retângulo.
//
// O ESCOPO É PROPOSITALMENTE MÍNIMO: M, L, C e Z, todos ABSOLUTOS. É
// exatamente o que o desenho da marca usa (conferido antes de escrever isto) e
// exatamente o que o PDF tem operador para: `m`, `l`, `c`, `h`. Arco elíptico
// (A) e comandos relativos ficam de fora — se um dia o logo mudar e passar a
// usá-los, `interpretarCaminho` lança em vez de desenhar errado em silêncio,
// que num documento entregue ao cliente é o pior desfecho possível.
// =============================================================================

export type Segmento =
  | { tipo: 'm'; x: number; y: number }
  | { tipo: 'l'; x: number; y: number }
  | { tipo: 'c'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { tipo: 'z' };

const COMANDOS_ACEITOS = new Set(['M', 'L', 'C', 'Z']);

/**
 * Quebra o `d` em segmentos. Números podem vir separados por espaço, vírgula
 * ou pelo próprio sinal de menos ("100-20" são dois números) — é como os
 * editores de SVG costumam exportar.
 */
export function interpretarCaminho(d: string): Segmento[] {
  const saida: Segmento[] = [];

  // TOKENIZADOR, e não "quebrar na letra": o `e` de "1e2" é notação
  // científica, não comando. Dividir por letra transformava `M0.5 1e2` em um
  // comando "e" inexistente — furo achado pelo próprio teste, 17/08/2026.
  const tokens = d.match(/[A-Za-z]|-?\d*\.?\d+(?:[eE][-+]?\d+)?/g) ?? [];

  // `^...$`, e não apenas [A-Za-z]: o token "1e2" CONTÉM uma letra sem ser um
  // comando. Testar "tem letra" fazia o parser recusar notação científica —
  // o segundo furo que o teste do `e` pegou, 17/08/2026.
  const ehComando = (t: string | undefined) => t !== undefined && /^[A-Za-z]$/.test(t);

  let i = 0;
  const proximoNumero = (comando: string): number => {
    const t = tokens[i];
    if (t === undefined || ehComando(t)) {
      throw new Error(`"${comando}" ficou sem número suficiente.`);
    }
    i += 1;
    return Number(t);
  };

  while (i < tokens.length) {
    const comando = tokens[i]!;
    if (!ehComando(comando)) {
      throw new Error(`Número solto fora de comando no desenho: "${comando}".`);
    }
    i += 1;

    if (!COMANDOS_ACEITOS.has(comando.toUpperCase())) {
      throw new Error(`Comando SVG não suportado no desenho: "${comando}".`);
    }
    if (comando !== comando.toUpperCase()) {
      throw new Error(`Comando relativo "${comando}" não é suportado — use absolutos.`);
    }

    if (comando === 'Z') { saida.push({ tipo: 'z' }); continue; }

    // Um comando vale para todos os números que vierem depois dele, até a
    // próxima letra — é a regra do SVG. Em M, só o primeiro par é "move": o
    // resto vira linha.
    let primeiro = true;
    do {
      if (comando === 'M' || comando === 'L') {
        const x = proximoNumero(comando);
        const y = proximoNumero(comando);
        saida.push({ tipo: primeiro && comando === 'M' ? 'm' : 'l', x, y });
      } else {
        saida.push({
          tipo: 'c',
          x1: proximoNumero(comando), y1: proximoNumero(comando),
          x2: proximoNumero(comando), y2: proximoNumero(comando),
          x: proximoNumero(comando), y: proximoNumero(comando),
        });
      }
      primeiro = false;
    } while (i < tokens.length && !ehComando(tokens[i]));
  }
  return saida;
}
